(() => {
  if (window.sykSocialChannels) {
    window.sykSocialChannels.init(document);
    return;
  }
  const instances = new Map();
  const timeoutMs = 15000;
  function consentAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return null;
    return typeof privacy.marketingAllowed === 'function' ? privacy.marketingAllowed() : true;
  }
  function privacyReady() {
    if (consentAllowed() !== null) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, 3000);
      if (!window.Shopify?.loadFeatures) return resolve();
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {
        clearTimeout(timer);
        resolve();
      });
    });
  }
  function create(root) {
    const tabs = [...root.querySelectorAll('[data-social-tab]')];
    const panels = [...root.querySelectorAll('[data-social-panel]')];
    const cleanup = [];
    let active = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.socialTab;
    let near = false;
    let generation = 0;
    let currentCleanup = () => {};
    function setStatus(panel, message) {
      const status = panel.querySelector('[data-social-status]');
      status.textContent = message;
      status.hidden = !message;
    }
    function showFallback(panel, message = '') {
      panel.querySelector('[data-social-fallback]').hidden = false;
      panel.dataset.socialState = message ? 'unavailable' : 'account-link';
      setStatus(panel, message);
    }
    function ready(panel) {
      panel.querySelector('[data-social-fallback]').hidden = true;
      panel.dataset.socialState = 'ready';
      setStatus(panel, '');
    }
    function stop() {
      generation += 1;
      currentCleanup();
      currentCleanup = () => {};
      panels.forEach((panel) => {
        panel.querySelectorAll('video').forEach((video) => video.pause());
        const mount = panel.querySelector('[data-social-mount]');
        if (mount) mount.replaceChildren();
      });
    }
    async function load() {
      if (!near) return;
      stop();
      const ownGeneration = generation;
      const panel = panels.find((item) => item.dataset.socialPanel === active);
      if (!panel) return;
      showFallback(panel);
      const curated = panel.querySelector('[data-social-curated]');
      if (curated?.children.length) {
        ready(panel);
        panel.dataset.socialState = 'curated';
        return;
      }
      if (active === 'instagram' && panel.querySelector('[data-social-instagram]').children.length) {
        const feed = panel.querySelector('[data-social-instagram]');
        const check = () => {
          const images = [...feed.querySelectorAll('img')];
          const media = feed.querySelector('.instafeed-container, [data-instafeed-item]');
          if (media || images.some((img) => img.complete && img.naturalWidth > 0)) ready(panel);
        };
        const observer = new MutationObserver(check);
        observer.observe(feed, { childList: true, subtree: true, attributes: true });
        feed.addEventListener('load', check, true);
        check();
        currentCleanup = () => {
          observer.disconnect();
          feed.removeEventListener('load', check, true);
        };
        return;
      }
      await privacyReady();
      if (ownGeneration !== generation) return;
      if (consentAllowed() === false) {
        showFallback(panel, 'Your privacy settings currently block embedded content. You can still open the channel above.');
        return;
      }
      const mount = panel.querySelector('[data-social-mount]');
      const platform = active;
      const cleanups = [];
      currentCleanup = () => cleanups.forEach((fn) => fn());
      const platformName = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube' }[platform];
      setStatus(panel, `Loading ${platformName}…`);
      const timer = setTimeout(() => {
        if (ownGeneration !== generation || panel.dataset.socialState === 'ready') return;
        mount.replaceChildren();
        showFallback(panel, 'Posts could not be loaded here. Open the channel above to see the latest.');
      }, timeoutMs);
      cleanups.push(() => clearTimeout(timer));
      if (platform === 'youtube') {
        const playlist = root.dataset.youtubePlaylist?.trim();
        if (!/^[A-Za-z0-9_-]{16,100}$/.test(playlist || '')) {
          clearTimeout(timer);
          showFallback(panel);
          return;
        }
        const url = new URL('https://www.youtube-nocookie.com/embed/videoseries');
        url.search = new URLSearchParams({ list: playlist, autoplay: '0', playsinline: '1', rel: '0', origin: location.origin });
        const frame = document.createElement('iframe');
        frame.title = 'SYOKAMI YouTube channel uploads';
        frame.src = url.href;
        frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        frame.allowFullscreen = true;
        frame.referrerPolicy = 'strict-origin-when-cross-origin';
        frame.addEventListener('load', () => {
          if (ownGeneration !== generation) return;
          clearTimeout(timer);
          ready(panel);
        }, { once: true });
        frame.addEventListener('error', () => {
          clearTimeout(timer);
          mount.replaceChildren();
          showFallback(panel, 'Open YouTube above to watch the latest films.');
        }, { once: true });
        mount.append(frame);
      } else if (platform === 'instagram') {
        let profile;
        try { profile = new URL(panel.dataset.profileUrl); } catch { clearTimeout(timer); return; }
        if (!/^(www\.)?instagram\.com$/.test(profile.hostname) || !/^\/[A-Za-z0-9._]+\/?$/.test(profile.pathname)) { clearTimeout(timer); return; }
        const quote = document.createElement('blockquote');
        quote.className = 'instagram-media';
        quote.dataset.instgrmPermalink = profile.href;
        quote.dataset.instgrmVersion = '14';
        const link = document.createElement('a');
        link.href = profile.href;
        link.textContent = 'View this profile on Instagram';
        quote.append(link);
        const observer = new MutationObserver(() => {
          const frame = mount.querySelector('iframe');
          if (!frame) return;
          observer.disconnect();
          frame.title = 'SYOKAMI Instagram profile and recent posts';
          frame.addEventListener('load', () => {
            if (ownGeneration !== generation) return;
            clearTimeout(timer);
            ready(panel);
          }, { once: true });
        });
        observer.observe(mount, { childList: true, subtree: true });
        cleanups.push(() => observer.disconnect());
        mount.append(quote);
        const script = document.createElement('script');
        script.src = 'https://www.instagram.com/embed.js';
        script.async = true;
        script.addEventListener('error', () => {
          clearTimeout(timer);
          mount.replaceChildren();
          showFallback(panel, 'Open Instagram above to see the latest posts.');
        }, { once: true });
        document.body.append(script);
        cleanups.push(() => script.remove());
      } else {
        let profile;
        try { profile = new URL(panel.dataset.profileUrl); } catch { clearTimeout(timer); return; }
        if (!/^(www\.)?tiktok\.com$/.test(profile.hostname)) { clearTimeout(timer); return; }
        const handle = profile.pathname.match(/^\/@([A-Za-z0-9._]+)\/?$/)?.[1];
        if (!handle) { clearTimeout(timer); return; }
        const quote = document.createElement('blockquote');
        quote.className = 'tiktok-embed';
        quote.cite = `https://www.tiktok.com/@${handle}`;
        quote.dataset.uniqueId = handle;
        quote.dataset.embedType = 'creator';
        quote.dataset.embedFrom = 'oembed';
        const section = document.createElement('section');
        const link = document.createElement('a');
        link.href = quote.cite;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = `@${handle}`;
        section.append(link);
        quote.append(section);
        const observer = new MutationObserver(() => {
          const frame = mount.querySelector('iframe');
          if (!frame) return;
          observer.disconnect();
          frame.title = 'SYOKAMI TikTok profile and recent videos';
          frame.addEventListener('load', () => {
            if (ownGeneration !== generation) return;
            clearTimeout(timer);
            ready(panel);
          }, { once: true });
        });
        observer.observe(mount, { childList: true, subtree: true });
        cleanups.push(() => observer.disconnect());
        mount.append(quote);
        const script = document.createElement('script');
        script.src = 'https://www.tiktok.com/embed.js';
        script.async = true;
        script.addEventListener('error', () => {
          clearTimeout(timer);
          mount.replaceChildren();
          showFallback(panel, 'Open TikTok above to see the latest films.');
        }, { once: true });
        document.body.append(script);
        cleanups.push(() => script.remove());
      }
    }
    function select(tab, focus = false) {
      active = tab.dataset.socialTab;
      tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset.socialPanel !== active; });
      if (focus) tab.focus();
      load();
    }
    const click = (event) => {
      const tab = event.target.closest('[data-social-tab]');
      if (tab && root.contains(tab)) select(tab);
    };
    const keydown = (event) => {
      const tab = event.target.closest('[data-social-tab]');
      if (!tab) return;
      let index = tabs.indexOf(tab);
      if (event.key === 'ArrowRight') index = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') index = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = tabs.length - 1;
      else return;
      event.preventDefault();
      select(tabs[index], true);
    };
    root.addEventListener('click', click);
    root.addEventListener('keydown', keydown);
    cleanup.push(() => root.removeEventListener('click', click), () => root.removeEventListener('keydown', keydown));
    const privacyChange = () => { if (near) load(); };
    document.addEventListener('visitorConsentCollected', privacyChange);
    cleanup.push(() => document.removeEventListener('visitorConsentCollected', privacyChange));
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      near = true;
      observer.disconnect();
      load();
    }, { rootMargin: '200px' });
    observer.observe(root);
    cleanup.push(() => observer.disconnect());
    return { destroy() { stop(); cleanup.forEach((fn) => fn()); } };
  }
  function init(scope) {
    const roots = [...scope.querySelectorAll('[data-social-channels]')];
    if (scope.matches?.('[data-social-channels]')) roots.unshift(scope);
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, create(root));
    });
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, instance] of instances) {
      if (event.target === root || event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    }
  });
  window.sykSocialChannels = { init };
  init(document);
})();
